import React, {useState} from 'react';
import {Store} from '../../models/store.model';
import {AppText, AppCardTitle, AppBodyText} from '../../../../Presentation/components/ui/Typography';
import {Image, Eye, ExternalLink} from 'lucide-react';

interface StoreImagesInfoProps {
  store: Store;
}

export const StoreImagesInfo: React.FC<StoreImagesInfoProps> = ({store}) => {
  const [showImageModal, setShowImageModal] = useState<{
    isOpen: boolean;
    imageUrl: string;
    imageIndex: number;
  }>({
    isOpen: false,
    imageUrl: '',
    imageIndex: 0
  });

  const handleImageClick = (imageUrl: string, index: number) => {
    setShowImageModal({
      isOpen: true,
      imageUrl,
      imageIndex: index
    });
  };

  const closeImageModal = () => {
    setShowImageModal({
      isOpen: false,
      imageUrl: '',
      imageIndex: 0
    });
  };

  const nextImage = () => {
    if (store.images && store.images.length > 0) {
      const nextIndex = (showImageModal.imageIndex + 1) % store.images.length;
      setShowImageModal({
        isOpen: true,
        imageUrl: store.images[nextIndex],
        imageIndex: nextIndex
      });
    }
  };

  const prevImage = () => {
    if (store.images && store.images.length > 0) {
      const prevIndex = showImageModal.imageIndex === 0
        ? store.images.length - 1
        : showImageModal.imageIndex - 1;
      setShowImageModal({
        isOpen: true,
        imageUrl: store.images[prevIndex],
        imageIndex: prevIndex
      });
    }
  };

  // Don't render if no images are available
  if (!store.images || store.images.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-4">
        <AppCardTitle className="border-b pb-2">صور المتجر</AppCardTitle>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {store.images.map((imageUrl, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={imageUrl}
                  alt={`صورة المتجر ${index + 1}`}
                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                  onClick={() => handleImageClick(imageUrl, index)}
                />
              </div>

              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleImageClick(imageUrl, index)}
                    className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all"
                    title="عرض الصورة"
                  >
                    <Eye className="w-4 h-4 text-gray-700" />
                  </button>
                  <a
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all"
                    title="فتح في تبويب جديد"
                  >
                    <ExternalLink className="w-4 h-4 text-gray-700" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <AppBodyText className="text-gray-600">
            {store.images.length} صورة متاحة
          </AppBodyText>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] overflow-hidden relative">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                صورة المتجر ({showImageModal.imageIndex + 1} من {store.images?.length})
              </h3>
              <button
                onClick={closeImageModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="relative p-4">
              <img
                src={showImageModal.imageUrl}
                alt={`صورة المتجر ${showImageModal.imageIndex + 1}`}
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />

              {/* Navigation arrows */}
              {store.images && store.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
                  >
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all"
                  >
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
